import React from "react";
import { useTranslation } from "react-i18next";

/**
 * v0.4.38 — DuplicateFileModal.
 *
 * Renders the rich "DUPLICATE FILE — CONFIRMATION REQUIRED"
 * dialog with three sections:
 *   1. Plain-English LIKELY CAUSE one-liner — computed by
 *      comparing the upload context (this filename + this
 *      adapter) against the prior run payload returned by the
 *      backend in `detail.prior_run`. The case for
 *      `is_test_fixture` rows is named explicitly so the
 *      operator does not panic when production data legitimately
 *      matches a seeded fixture.
 *   2. Side-by-side compare table (This upload / Prior upload).
 *   3. Existing technical SHA-256 paragraph from the backend.
 *
 * Extracted from UploadPanel.jsx (v0.4.38 N5) to keep the
 * parent file under the 400-LOC cap.
 *
 * Existing test-ids preserved:
 *   duplicate-file-modal, duplicate-modal-cancel, duplicate-modal-force
 * New test-ids:
 *   duplicate-likely-cause, duplicate-compare-table
 */

const ADAPTER_LABELS = {
  client_template: "Client template",
};
const adapterLabel = (k) => ADAPTER_LABELS[k] || k || "—";

const shortRunId = (id) => (id ? String(id).slice(0, 8) : "—");

const formatPriorTimestamp = (ts) => {
  if (!ts) return "—";
  // backend returns ISO strings — slice to "YYYY-MM-DD HH:MM"
  return String(ts).slice(0, 16).replace("T", " ");
};

const computeLikelyCause = ({ thisFilename, thisAdapter, prior }) => {
  if (!prior) {
    return "Same file bytes detected — confirm before forcing.";
  }
  if (prior.is_test_fixture) {
    return (
      "This file matches a test fixture already in the system. " +
      "If this is real production data with the same content as " +
      "a test file, force re-upload is safe."
    );
  }
  const sameName = (prior.filename || "") === (thisFilename || "");
  const sameAdapter = (prior.adapter || "") === (thisAdapter || "");
  if (sameName && sameAdapter) {
    return (
      "Same file, same adapter, same name — you have already " +
      "uploaded this exact file."
    );
  }
  if (!sameAdapter && sameName) {
    return (
      `Same file bytes, different adapter (you picked ` +
      `${adapterLabel(thisAdapter)}, prior upload used ` +
      `${adapterLabel(prior.adapter)}). Switching adapter does ` +
      "not change the file bytes — this would still double-count."
    );
  }
  if (sameAdapter && !sameName) {
    return (
      `Same file bytes, different filename (this: ` +
      `${thisFilename || "—"}, prior: ${prior.filename || "—"}). ` +
      "Looks like the same export was renamed."
    );
  }
  return (
    "Same file bytes, different adapter AND different filename — " +
    "confirm before forcing."
  );
};

const CompareRow = ({ label, left, right, leftAccent, rightAccent }) => (
  <tr className="border-t border-slate-200 dark:border-slate-700">
    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 uppercase text-[10px] tracking-widest">
      {label}
    </td>
    <td className={[
      "px-3 py-2 tabular-nums text-sm",
      leftAccent ? "text-amber-700 dark:text-amber-300" : "text-slate-900 dark:text-slate-50",
    ].join(" ")}>
      {left || "—"}
    </td>
    <td className={[
      "px-3 py-2 tabular-nums text-sm",
      rightAccent ? "text-amber-700 dark:text-amber-300" : "text-slate-900 dark:text-slate-50",
    ].join(" ")}>
      {right || "—"}
    </td>
  </tr>
);

export const DuplicateFileModal = ({
  open, prior, message, thisFilename, thisAdapter, busy,
  onCancel, onForce,
}) => {
  const { t } = useTranslation();
  if (!open) return null;
  const likelyCause = computeLikelyCause({
    thisFilename, thisAdapter, prior,
  });
  const nameDiffers = prior && (prior.filename || "") !== (thisFilename || "");
  const adapterDiffers = prior && (prior.adapter || "") !== (thisAdapter || "");
  return (
    <div
      data-testid="duplicate-file-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-4"
    >
      <div className="bg-white dark:bg-slate-900 border border-amber-600/60 max-w-2xl w-full p-5 space-y-4">
        <div className="text-xs tracking-normal text-amber-700 dark:text-amber-300">
          {t("ui.upload.duplicate_modal_title")}
        </div>

        <div
          data-testid="duplicate-likely-cause"
          className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-900 dark:text-amber-100 leading-relaxed"
        >
          <span className="text-[10px] tracking-normal text-amber-700 dark:text-amber-300 block mb-1">
            {t("ui.upload.duplicate_likely_cause", { defaultValue: "Likely cause" })}
          </span>
          {likelyCause}
        </div>

        <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
          <table
            data-testid="duplicate-compare-table"
            className="w-full text-sm"
          >
            <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-[10px] tracking-normal">
              <tr>
                <th className="px-3 py-2 text-left">{t("ui.upload.duplicate_col_field", { defaultValue: "Field" })}</th>
                <th className="px-3 py-2 text-left">{t("ui.upload.duplicate_col_this", { defaultValue: "This upload" })}</th>
                <th className="px-3 py-2 text-left">
                  {t("ui.upload.duplicate_col_prior", { defaultValue: "Prior upload" })}{prior?.id ? ` (${shortRunId(prior.id)})` : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              <CompareRow
                label={t("ui.upload.duplicate_row_filename", { defaultValue: "Filename" })}
                left={thisFilename}
                right={prior?.filename}
                leftAccent={nameDiffers}
                rightAccent={nameDiffers}
              />
              <CompareRow
                label={t("ui.upload.duplicate_row_adapter", { defaultValue: "Adapter" })}
                left={adapterLabel(thisAdapter)}
                right={adapterLabel(prior?.adapter)}
                leftAccent={adapterDiffers}
                rightAccent={adapterDiffers}
              />
              <CompareRow
                label={t("ui.upload.duplicate_row_uploaded_at", { defaultValue: "Uploaded at" })}
                left={t("ui.upload.duplicate_now", { defaultValue: "now" })}
                right={formatPriorTimestamp(prior?.created_at)}
              />
              <CompareRow
                label={t("ui.upload.duplicate_row_rows_accepted", { defaultValue: "Rows accepted" })}
                left="—"
                right={prior?.rows_accepted != null
                  ? String(prior.rows_accepted) : "—"}
              />
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed whitespace-pre-line border-l-2 border-slate-200 dark:border-slate-700 pl-3">
          {message}
        </div>

        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            data-testid="duplicate-modal-cancel"
            onClick={onCancel}
            autoFocus
            className="text-xs border border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 px-4 py-2 rounded-xl"
          >
            {t("ui.upload.duplicate_modal_cancel")}
          </button>
          <button
            type="button"
            data-testid="duplicate-modal-force"
            onClick={onForce}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-600 disabled:opacity-40 text-white font-semibold text-xs px-4 py-2 rounded-xl"
          >
            {t("ui.upload.duplicate_modal_force")}
          </button>
        </div>
      </div>
    </div>
  );
};
